"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth, useUser, useSignIn, useClerk } from "@clerk/nextjs";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiFetchAuthed } from "@/lib/api";
import { ENV_PREFIX } from "@/lib/query-keys";
import { scheduleIdle } from "@/lib/idle";
import { eventDetailLog as log } from "@/lib/logger";
import { useScrollEffects } from "@/app/(events)/[id]/[slug]/components/useScrollEffects";
import { useEventActions } from "@/app/(events)/[id]/[slug]/components/useEventActions";
import { loadRazorpayScript } from "@/app/(events)/[id]/[slug]/hooks/useEventHandlers";

import type {
  PublicEvent,
  RsvpStatus,
  SuperGuestSummary,
} from "@/app/(events)/[id]/[slug]/components/EventTypes";
import {
  DEFAULT_URGENCY_CONFIG,
  formatCurrencyText,
  parseUrgencyConfig,
} from "@/app/(events)/[id]/[slug]/components/EventTypes";

export function useEventPage(initialEvent?: PublicEvent | null) {
  const { getToken, isLoaded, isSignedIn, signOut } = useAuth();
  const { user } = useUser();
  const { signIn } = useSignIn();
  const { setActive } = useClerk();
  const params = useParams();
  const router = useRouter();
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();
  const eventId = typeof params?.id === "string" ? params.id : "";
  const inviteId = searchParams?.get("invite") ?? "";

  /* ------------------------------------------------------------------ */
  /*  Data queries                                                       */
  /* ------------------------------------------------------------------ */

  const { data: event, isLoading, error } = useQuery({
    queryKey: [ENV_PREFIX, "event", eventId],
    queryFn: async () => {
      const stop = log.time("fetchEvent");
      try {
        const data = await apiFetch<PublicEvent>(`events/${eventId}`, { cache: "no-store" });
        stop();
        log.info("fetchEvent", "loaded", { eventId, title: data.title, status: data.status });
        return data;
      } catch (err) {
        stop();
        log.error("fetchEvent", "failed", { eventId, error: err instanceof Error ? err.message : String(err) });
        throw err;
      }
    },
    enabled: Boolean(eventId),
    initialData: initialEvent ?? undefined,
    staleTime: initialEvent ? 30_000 : 0,
    gcTime: 30_000,
  });

  const { data: urgencyConfigData } = useQuery({
    queryKey: [ENV_PREFIX, "promotions-urgency-config"],
    queryFn: async () => {
      const data = await apiFetch<unknown>("public/config/promotions/urgency-tiers");
      const parsed = parseUrgencyConfig(data);
      return parsed ?? DEFAULT_URGENCY_CONFIG;
    },
    staleTime: 60_000,
  });

  const errorMessage = error instanceof Error ? error.message : "Unable to load event.";

  /* ------------------------------------------------------------------ */
  /*  State                                                              */
  /* ------------------------------------------------------------------ */

  const [rsvpState, setRsvpState] = useState<
    "idle" | "loading" | "confirmed" | "approved" | "pending" | "waitlist" | "rejected" | "cancelled" | "error"
  >("idle");
  const [rsvpError, setRsvpError] = useState<string | null>(null);
  const [rsvpStatus, setRsvpStatus] = useState<RsvpStatus>("NONE");
  const [rsvpStatusLoaded, setRsvpStatusLoaded] = useState(false);
  const [superGuest, setSuperGuest] = useState<SuperGuestSummary | null>(null);
  const [dbEmail, setDbEmail] = useState<string | null>(null);
  const [savedSocialLinks, setSavedSocialLinks] = useState<Record<string, string>>({});
  const [inviteInput, setInviteInput] = useState("");
  const [inviteStatus, setInviteStatus] = useState<"idle" | "sending" | "sent">("idle");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMessage, setAuthModalMessage] = useState<string | null>(null);
  const [showProfileGate, setShowProfileGate] = useState(false);
  const [showInAppBrowserWarning, setShowInAppBrowserWarning] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [attendeesOpen, setAttendeesOpen] = useState(false);
  const [attendees, setAttendees] = useState<{ id: string; name: string; imageUrl: string | null }[]>([]);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportNotes, setReportNotes] = useState("");
  const [reportEmail, setReportEmail] = useState("");
  const [reportStatus, setReportStatus] = useState<"idle" | "submitting" | "sent" | "error">("idle");
  const [reportError, setReportError] = useState<string | null>(null);
  const [ticketPickerOpen, setTicketPickerOpen] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState<{ id: string; quantity: number }[]>([]);
  const [promoCode, setPromoCode] = useState("");
  const [promoValidation, setPromoValidation] = useState<{ valid: boolean; discountCents?: number; discountType?: string; discountValue?: number; finalAmount?: number; currency?: string; reason?: string } | null>(null);
  const [promoValidating, setPromoValidating] = useState(false);
  const [dummyPaymentOpen, setDummyPaymentOpen] = useState(false);
  const [dummyPaymentOrder, setDummyPaymentOrder] = useState<{ orderId: string; currency: string } | null>(null);
  const [dummyPaymentError, setDummyPaymentError] = useState<string | null>(null);
  const [dummyPaymentLoading, setDummyPaymentLoading] = useState(false);
  const [cashOnlyOpen, setCashOnlyOpen] = useState(false);
  const [failedPaymentInfo, setFailedPaymentInfo] = useState<{ orderId: string; paymentId: string; signature: string } | null>(null);
  const [retryingVerification, setRetryingVerification] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [checkoutDebounced, setCheckoutDebounced] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<"select" | "form" | "review" | "guest-info">("select");
  const [formResponses, setFormResponses] = useState<Record<string, unknown>>({});
  const [pendingOrder, setPendingOrder] = useState<{
    keyId: string;
    orderId: string;
    amount: number;
    currency: string;
    priceBreakdown?: {
      ticketBaseCents: number;
      coverChargeCents: number;
      ticketGstCents: number;
      platformFeeBaseCents: number;
      platformFeeGstCents: number;
      platformFeeGstRate?: number;
      discountCents: number;
      finalAmountCents: number;
      gstRate?: number;
    };
  } | null>(null);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketQrUrl, setTicketQrUrl] = useState<string | null>(null);
  const [ticketCode, setTicketCode] = useState<string | null>(null);
  const [ticketRsvpId, setTicketRsvpId] = useState<string | null>(null);
  const [purchasedTickets, setPurchasedTickets] = useState<{ rsvpId: string; ticketCode: string | null; tierLabel: string | null }[]>([]);
  const [showBuyerForm, setShowBuyerForm] = useState(false);
  const [formFields, setFormFields] = useState<{ id: string; type: string; label: string; required: boolean; placeholder?: string; helpText?: string; options?: string[]; validation?: Record<string, unknown>; sortOrder: number }[]>([]);
  const [pendingTicketForForm, setPendingTicketForForm] = useState<{ code: string | null; rsvpId: string | null; tickets?: { rsvpId: string; ticketCode: string | null; tierLabel: string | null }[] } | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [heroSlide, setHeroSlide] = useState(0);
  const [descExpanded, setDescExpanded] = useState(false);
  const [expandedPerformerId, setExpandedPerformerId] = useState<string | null>(null);
  const [showAllPerformers, setShowAllPerformers] = useState(false);

  // Ref for formResponses so async handlers always read the latest value
  const formResponsesRef = useRef<Record<string, unknown>>(formResponses);
  useEffect(() => {
    formResponsesRef.current = formResponses;
  }, [formResponses]);

  const ticketOptionsRef = useRef<HTMLDivElement | null>(null);
  const rsvpInFlightRef = useRef(false);
  const checkoutInFlightRef = useRef(false);
  const confirmPaymentInFlightRef = useRef(false);

  /* ------------------------------------------------------------------ */
  /*  Hooks                                                              */
  /* ------------------------------------------------------------------ */

  const { backgroundRef, isActionStacked, scrolledPastHero, scrollY } = useScrollEffects();
  const {
    saved,
    setSaved,
    saving,
    handleShare,
    handleWhatsAppShare,
    handleAddToCalendar,
    handleToggleSave,
  } = useEventActions(event);

  const hostProfileKey = event?.hostPublicUsername ?? event?.hostProfileId ?? null;

  /* ------------------------------------------------------------------ */
  /*  Effects                                                            */
  /* ------------------------------------------------------------------ */

  // Reset all UI state when navigating between events
  /* eslint-disable react-hooks/set-state-in-effect -- intentional reset on navigation, not derived state */
  useEffect(() => {
    setHeroSlide(0);
    setGalleryIndex(0);
    setGalleryOpen(false);
    setDescExpanded(false);
    setExpandedPerformerId(null);
    setShowAllPerformers(false);
  }, [eventId]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Page mount logging
  useEffect(() => {
    log.info("mount", "page loaded", { eventId, inviteId: inviteId || undefined, isSignedIn });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Combined /me endpoint: fetches rsvp status, super-guest info, and saved state in a single call
  useEffect(() => {
    if (!eventId || !isLoaded) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- marks rsvp load finished for signed-out users, not derived state
    if (!isSignedIn) { setRsvpStatusLoaded(true); return; }
    const loadMe = async () => {
      log.debug("loadMe", "fetching", { eventId });
      const stop = log.time("loadMe");
      try {
        const data = await apiFetchAuthed<{
          rsvp: { status: RsvpStatus; rsvpId?: string; ticketCode?: string } | null;
          superGuest: SuperGuestSummary | null;
          saved: boolean;
          email: string | null;
          socialLinks?: Record<string, string>;
        }>(`events/${eventId}/me`, {}, () => getToken());
        stop();

        // Map rsvp status
        const status = data.rsvp?.status ?? "NONE";
        log.info("loadMe", "loaded", { rsvpStatus: status, isSuperGuest: data.superGuest?.isSuperGuest, saved: data.saved });
        setRsvpStatus(status);
        if (status === "CONFIRMED") setRsvpState("confirmed");
        else if (status === "APPROVED") setRsvpState("approved");
        else if (status === "WAITLIST") setRsvpState("waitlist");
        else if (status === "PENDING") setRsvpState("pending");
        else if (status === "REJECTED") setRsvpState("rejected");
        else if (status === "CANCELLED") setRsvpState("cancelled");
        else setRsvpState("idle");
        setRsvpStatusLoaded(true);

        // Map super-guest info
        setSuperGuest(data.superGuest ?? null);

        // Map saved state
        setSaved(Boolean(data.saved));

        // Store DB email for profile completeness check
        setDbEmail(data.email ?? null);
        setSavedSocialLinks(data.socialLinks ?? {});
      } catch (err) {
        stop();
        log.warn("loadMe", "failed", { error: err instanceof Error ? err.message : String(err) });
        // Ensure rsvpStatusLoaded is set even on failure so UI doesn't hang
        setRsvpStatusLoaded(true);
        setSuperGuest(null);
      }
    };
    void loadMe();
  }, [eventId, getToken, isLoaded, isSignedIn, setSaved]);

  // Restore pending checkout intent after user signs in
  /* eslint-disable react-hooks/set-state-in-effect -- imperative: reads sessionStorage, not derived state */
  useEffect(() => {
    if (!isSignedIn) return;
    try {
      const raw = sessionStorage.getItem("hopp_pending_checkout");
      if (!raw) return;
      const intent = JSON.parse(raw) as {
        eventId?: string;
        selectedTickets?: { id: string; quantity: number }[];
        selectedTicketId?: string | null;
        ticketQuantity?: number;
        promoCode?: string;
      };
      if (intent.eventId !== eventId) return;
      sessionStorage.removeItem("hopp_pending_checkout");
      if (intent.selectedTickets && Array.isArray(intent.selectedTickets) && intent.selectedTickets.length > 0) {
        setSelectedTickets(intent.selectedTickets);
      } else if (intent.selectedTicketId) {
        setSelectedTickets([{ id: intent.selectedTicketId, quantity: Math.max(1, intent.ticketQuantity ?? 1) }]);
      }
      if (intent.promoCode) setPromoCode(intent.promoCode);
      setTicketPickerOpen(true);
    } catch { /* malformed data or storage unavailable — ignore */ }
  }, [isSignedIn, eventId]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const isModalOpen = showAuthModal || showProfileGate || reportOpen || attendeesOpen || ticketPickerOpen || dummyPaymentOpen || galleryOpen;

  useEffect(() => {
    if (!isModalOpen) return;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (!inviteId) return;
    const trackInvite = async () => {
      try { await apiFetch(`invites/${inviteId}/open`, { method: "POST" }); } catch { /* best-effort */ }
    };
    void trackInvite();
  }, [inviteId]);

  // Refetch full event data WITH auth when user has access but event was loaded as restricted
  useEffect(() => {
    if (event && "restricted" in event && event.restricted && (rsvpState === "approved" || rsvpState === "confirmed") && isSignedIn) {
      const refetchWithAuth = async () => {
        try {
          const data = await apiFetchAuthed<PublicEvent>(`events/${eventId}`, { cache: "no-store" }, () => getToken());
          queryClient.setQueryData([ENV_PREFIX, "event", eventId], data);
        } catch { /* leave restricted view */ }
      };
      void refetchWithAuth();
    }
  }, [event, rsvpState, queryClient, eventId, isSignedIn, getToken]);

  /* ------------------------------------------------------------------ */
  /*  Derived values                                                     */
  /* ------------------------------------------------------------------ */

  const dateLabel = formatDate(event?.date);
  const timeLabel = formatTime(event?.time);
  const hostLabel = event?.host?.trim() ? event.host : "Host";
  const eventKey = event?.id ?? eventId;

  const priceLabel = (() => {
    const price = event?.price?.trim();
    return price ? formatCurrencyText(price) ?? price : "--";
  })();

  const ticketOptions = useMemo(() => {
    if (!event) return [] as { id: string; label: string; price: string | null; originalPrice: string | null; description: string | null; quantityTotal: number | null; quantitySold: number | null; startsAt: string | null; expiresAt: string | null; tierStatus: "active" | "upcoming" | "expired" | "sold_out" }[];
    const now = new Date();
    if (event.ticketCategories && event.ticketCategories.length > 0) {
      const ticketOnlyCategories = event.ticketCategories.filter((c) => c.type !== "COVER_CHARGE");
      if (ticketOnlyCategories.length > 0) {
        return ticketOnlyCategories.map((category) => {
          let tierStatus: "active" | "upcoming" | "expired" | "sold_out" = "active";
          if (typeof category.quantityTotal === "number" && typeof category.quantitySold === "number" && category.quantitySold >= category.quantityTotal) tierStatus = "sold_out";
          else if (category.expiresAt && new Date(category.expiresAt) < now) tierStatus = "expired";
          else if (category.startsAt && new Date(category.startsAt) > now) tierStatus = "upcoming";
          return { id: category.id, label: category.label, price: category.price ?? null, originalPrice: category.originalPrice ?? null, description: category.description ?? null, quantityTotal: category.quantityTotal ?? null, quantitySold: category.quantitySold ?? null, startsAt: category.startsAt ?? null, expiresAt: category.expiresAt ?? null, tierStatus };
        });
      }
    }
    return [{ id: "general", label: "General admission", price: event.price ?? null, originalPrice: event.originalPrice ?? null, description: null, quantityTotal: null, quantitySold: null, startsAt: null, expiresAt: null, tierStatus: "active" as const }];
  }, [event]);

  const coverChargeCategory = useMemo(() => event?.ticketCategories?.find((c) => c.type === "COVER_CHARGE") ?? null, [event]);

  const lowestPriceCategory = useMemo(() => {
    const categories = event?.ticketCategories?.filter((c) => c.type !== "COVER_CHARGE");
    if (!categories || categories.length === 0) return null;
    const pricedTiers = categories.filter((category) => typeof category.priceCents === "number");
    if (pricedTiers.length > 0) {
      return pricedTiers.reduce((lowest, current) =>
        (current.inclusivePriceCents ?? current.priceCents ?? 0) < (lowest.inclusivePriceCents ?? lowest.priceCents ?? 0) ? current : lowest
      );
    }
    return categories[0] ?? null;
  }, [event]);

  const isPaidEvent = useMemo(() => {
    const categories = event?.ticketCategories?.filter((c) => c.type !== "COVER_CHARGE") ?? [];
    if (categories.length > 0) return categories.some((c) => typeof c.priceCents === "number" && c.priceCents > 0);
    const rawPrice = event?.price ?? "";
    const numeric = Number(rawPrice.replace(/[^\d]/g, ""));
    return Number.isFinite(numeric) && numeric > 0;
  }, [event]);

  const startingFromPriceLabel = useMemo(() => {
    if (lowestPriceCategory) {
      const inclusiveCents = lowestPriceCategory.inclusivePriceCents;
      if (typeof inclusiveCents === "number") {
        if (inclusiveCents === 0) return "Free";
        return `\u20B9${(inclusiveCents / 100).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      }
      if (typeof lowestPriceCategory.priceCents === "number" && lowestPriceCategory.priceCents === 0) return "Free";
      if (lowestPriceCategory.price) {
        const formatted = formatCurrencyText(lowestPriceCategory.price) ?? lowestPriceCategory.price;
        if (formatted === "₹0" || formatted === "0") return "Free";
        return formatted;
      }
    }
    return priceLabel;
  }, [lowestPriceCategory, priceLabel]);

  // Late Night pill: only renders when the event is explicitly categorised as a
  // late-night event by the host. Time-of-day inference produced false positives.
  const moodTag: string | null = ((): string | null => {
    const category = event?.category?.trim();
    const tags = event?.tags ?? [];
    const hasLateNightTag =
      (category && /late\s*night/i.test(category)) ||
      tags.some((t) => typeof t === "string" && /late\s*night/i.test(t));
    if (!hasLateNightTag) return null;
    const base = category ?? (event?.eventType === "ONLINE" ? "Online" : "Event");
    return `${base} • Late Night`;
  })();

  const hasTicketTiers = Boolean(event?.ticketCategories && event.ticketCategories.filter((c) => c.type !== "COVER_CHARGE").length > 1);

  const hasActiveSelected = useMemo(
    () => selectedTickets.some((t) => ticketOptions.find((o) => o.id === t.id)?.tierStatus === "active"),
    [ticketOptions, selectedTickets],
  );
  const canContinueCheckout = Boolean(selectedTickets.length > 0 && hasActiveSelected);

  const isOnlineEvent = event?.eventType === "ONLINE";
  const isRestricted = Boolean(event && "restricted" in event && event.restricted && rsvpState !== "approved" && rsvpState !== "confirmed");
  // True when the event object still carries the restricted payload (no full data yet), regardless of rsvpState
  const eventStillRestricted = Boolean(event && "restricted" in event && (event as Record<string, unknown>).restricted);

  const sortedPerformers = useMemo(() => {
    const performers = event?.performers ?? [];
    if (performers.length === 0) return [];
    return [...performers].sort((a, b) => {
      if (a.isHeadliner !== b.isHeadliner) return a.isHeadliner ? -1 : 1;
      const aTime = a.performanceStartTime ? new Date(a.performanceStartTime).getTime() : Infinity;
      const bTime = b.performanceStartTime ? new Date(b.performanceStartTime).getTime() : Infinity;
      if (aTime !== bTime) return aTime - bTime;
      return a.name.localeCompare(b.name);
    });
  }, [event]);

  const mediaItems = event?.media ?? [];
  const heroImages = useMemo(() => {
    const media = event?.media ?? [];
    const heroImage = event?.heroImage ?? null;
    const images = media.filter((m) => m.kind === "IMAGE");
    if (images.length > 0) return images;
    if (heroImage) return [{ id: "hero", kind: "IMAGE", url: heroImage, label: null }];
    return [] as { id: string; kind: string; url: string; label: string | null }[];
  }, [event]);
  const stages = event?.stages ?? [];
  const hasMultiStage = stages.length > 1;

  const isFreeEvent = useMemo(() => {
    const cats = event?.ticketCategories ?? [];
    if (cats.length === 0) {
      const rawPrice = event?.price ?? "";
      const numeric = Number(rawPrice.replace(/[^\d]/g, ""));
      return !Number.isFinite(numeric) || numeric === 0;
    }
    return cats.every((c) => !c.priceCents || c.priceCents === 0);
  }, [event]);

  const showPaymentMethods = useMemo(() => {
    if (isFreeEvent) return false;
    const methods = event?.acceptedPaymentMethods ?? [];
    if (methods.length === 0) return false;
    if (methods.length === 1 && methods[0] === "CASH") return false;
    return true;
  }, [event, isFreeEvent]);

  const hostStats = event?.hostStats ?? null;
  const ratingLabel = hostStats?.ratingCount ? `${hostStats.ratingAverage.toFixed(1)} (${hostStats.ratingCount})` : "New host";

  useEffect(() => {
    if (!event) return;
    return scheduleIdle(() => {
      if (hostProfileKey) {
        void apiFetch(`hosts/${hostProfileKey}`).catch(() => {});
        router.prefetch(`/hosts/${hostProfileKey}`);
      }
      router.prefetch("/onboarding");
      router.prefetch("/legal/terms");
      router.prefetch("/legal/privacy");
      // Preload Razorpay script for paid events so checkout is instant
      if (isPaidEvent) void loadRazorpayScript().catch(() => {});
    }, 1200);
  }, [event, hostProfileKey, router, isPaidEvent]);

  // Auto-select first active ticket when ticket options change
  /* eslint-disable react-hooks/set-state-in-effect -- initializes interactive state from data, not derived state */
  useEffect(() => {
    if (ticketOptions.length === 0) return;
    const firstActive = ticketOptions.find((o) => o.tierStatus === "active");
    setSelectedTickets((prev) => {
      const hasActive = prev.some((t) => ticketOptions.some((o) => o.id === t.id && o.tierStatus === "active"));
      if (hasActive) return prev;
      if (firstActive) return [{ id: firstActive.id, quantity: 1 }];
      return [];
    });
  }, [ticketOptions]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const tags = event?.tags ?? [];

  const ruleItems = useMemo(() => {
    const rules = event?.rules;
    if (!rules) return [] as { label: string; tone: "ok" | "warn" }[];
    const items: { label: string; tone: "ok" | "warn" }[] = [];
    if (rules.rule21Plus) items.push({ label: "Age restriction: 21+ only. ID required.", tone: "warn" });
    if (rules.ruleSmartCasual) items.push({ label: "Dress code: Smart casuals.", tone: "ok" });
    if (rules.ruleCashBar) items.push({ label: "Cash bar available.", tone: "ok" });
    if (rules.ruleNoPlusOnes) items.push({ label: "No plus ones.", tone: "warn" });
    if (rules.ruleNoPhotos) items.push({ label: "No photos allowed.", tone: "warn" });
    if (rules.ruleShoesOff) items.push({ label: "Shoes off indoors.", tone: "warn" });
    (rules.customRules ?? []).forEach((rule) => { if (rule.trim()) items.push({ label: rule, tone: "ok" }); });
    return items;
  }, [event]);

  const mapCoordinates = useMemo(() => {
    if (!event || typeof event.locationLat !== "number" || typeof event.locationLng !== "number") return null;
    return { lat: event.locationLat, lng: event.locationLng };
  }, [event]);

  const mapQuery = (() => {
    if (!event) return "";
    return event.locationFormattedAddress ?? event.locationPlaceName ?? [event.venue, event.location, event.locationDetails].filter(Boolean).join(" ");
  })();

  const mapUrl = useMemo(() => {
    if (mapCoordinates) return `https://www.google.com/maps/search/?api=1&query=${mapCoordinates.lat},${mapCoordinates.lng}`;
    if (!mapQuery) return "";
    const encoded = encodeURIComponent(mapQuery);
    const placeId = event?.locationPlaceId;
    if (placeId) return `https://www.google.com/maps/search/?api=1&query=${encoded}&query_place_id=${placeId}`;
    return `https://www.google.com/maps/search/?api=1&query=${encoded}`;
  }, [mapQuery, mapCoordinates, event]);

  const mapsKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ?? "";
  const mapImageUrl = useMemo(() => {
    if (!mapsKey || (!mapCoordinates && !mapQuery)) return "";
    const params = new URLSearchParams({ zoom: "15", size: "640x360", scale: "2", maptype: "roadmap", key: mapsKey });
    if (mapCoordinates) {
      const center = `${mapCoordinates.lat},${mapCoordinates.lng}`;
      params.set("center", center);
      params.set("markers", `color:0x3A5CFF|${center}`);
    } else if (mapQuery) {
      params.set("center", mapQuery);
      params.set("markers", `color:0x3A5CFF|${mapQuery}`);
    }
    return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
  }, [mapQuery, mapCoordinates, mapsKey]);

  const capacityTotal = typeof event?.capacity === "number" ? event.capacity : null;
  const spotsLeft = typeof event?.spotsLeft === "number" ? event.spotsLeft : null;
  const isPromoted = event?.isPromoted === true;
  const urgencyConfig = urgencyConfigData ?? DEFAULT_URGENCY_CONFIG;
  const ticketsSold = capacityTotal !== null && spotsLeft !== null ? Math.max(capacityTotal - spotsLeft, 0) : null;
  const percentSold = capacityTotal !== null && capacityTotal > 0 && ticketsSold !== null ? Math.max(0, Math.min(100, (ticketsSold / capacityTotal) * 100)) : null;

  const urgencyTier = useMemo(() => {
    if (!isPromoted || percentSold === null) return null;
    const tiers = urgencyConfig.tiers;
    if (!Array.isArray(tiers) || tiers.length === 0) return null;
    for (let i = 0; i < tiers.length; i += 1) {
      const tier = tiers[i];
      const isLast = i === tiers.length - 1;
      if (percentSold >= tier.minPercent && (isLast ? percentSold <= tier.maxPercent : percentSold < tier.maxPercent)) return tier;
    }
    return tiers[tiers.length - 1] ?? null;
  }, [isPromoted, percentSold, urgencyConfig]);

  const capacityLeftLabel = spotsLeft !== null ? String(spotsLeft) : "--";
  const capacityTotalLabel = capacityTotal !== null ? String(capacityTotal) : "--";

  const isApprovalEvent = event?.visibility === "PRIVATE" || event?.accessControl === "INVITE_ONLY" || event?.requiresHostApproval;
  const needsRequest = isApprovalEvent && rsvpState !== "approved" && rsvpState !== "confirmed";
  const isEnded = event?.status === "PAST";
  const isCancelled = event?.status === "CANCELLED";
  const canRsvp = event?.status !== "PAST" && event?.status !== "CANCELLED" && event?.status !== "REVIEW_PENDING" && event?.status !== "DRAFT";
  const primaryCtaLabel = needsRequest || isPaidEvent ? "Book Now" : "Attend";
  const invitesLeft = superGuest?.isSuperGuest ? superGuest?.invitesLeft ?? 0 : 0;
  const canInvite = Boolean(superGuest?.isSuperGuest) && invitesLeft > 0;

  return {
    // Auth
    getToken, isLoaded, isSignedIn, user,
    // Navigation
    router, eventId, inviteId,
    // Query
    event, isLoading, error, errorMessage, queryClient,
    // State
    rsvpState, setRsvpState, rsvpError, setRsvpError, rsvpStatus, setRsvpStatus, rsvpStatusLoaded,
    superGuest, setSuperGuest, dbEmail, setDbEmail, savedSocialLinks, setSavedSocialLinks,
    inviteInput, setInviteInput, inviteStatus, setInviteStatus, inviteError, setInviteError,
    showAuthModal, setShowAuthModal, authModalMessage, setAuthModalMessage,
    showProfileGate, setShowProfileGate,
    signIn, setActive, signOut,
    showInAppBrowserWarning, setShowInAppBrowserWarning, copiedLink, setCopiedLink,
    attendeesOpen, setAttendeesOpen, attendees, setAttendees, attendeesLoading, setAttendeesLoading,
    reportOpen, setReportOpen, reportReason, setReportReason, reportNotes, setReportNotes,
    reportEmail, setReportEmail, reportStatus, setReportStatus, reportError, setReportError,
    ticketPickerOpen, setTicketPickerOpen,
    selectedTickets, setSelectedTickets,
    promoCode, setPromoCode, promoValidation, setPromoValidation, promoValidating, setPromoValidating,
    dummyPaymentOpen, setDummyPaymentOpen, dummyPaymentOrder, setDummyPaymentOrder,
    dummyPaymentError, setDummyPaymentError, dummyPaymentLoading, setDummyPaymentLoading,
    cashOnlyOpen, setCashOnlyOpen,
    failedPaymentInfo, setFailedPaymentInfo, retryingVerification, setRetryingVerification,
    cancelConfirmOpen, setCancelConfirmOpen,
    checkoutDebounced, setCheckoutDebounced,
    checkoutStep, setCheckoutStep, formResponses, setFormResponses, pendingOrder, setPendingOrder,
    ticketModalOpen, setTicketModalOpen, ticketQrUrl, setTicketQrUrl,
    ticketCode, setTicketCode, ticketRsvpId, setTicketRsvpId,
    purchasedTickets, setPurchasedTickets,
    showBuyerForm, setShowBuyerForm, formFields, setFormFields,
    pendingTicketForForm, setPendingTicketForForm,
    galleryOpen, setGalleryOpen, galleryIndex, setGalleryIndex,
    heroSlide, setHeroSlide, descExpanded, setDescExpanded,
    expandedPerformerId, setExpandedPerformerId, showAllPerformers, setShowAllPerformers,
    // Refs
    ticketOptionsRef, rsvpInFlightRef, checkoutInFlightRef, confirmPaymentInFlightRef, formResponsesRef,
    // Scroll/actions hooks
    backgroundRef, isActionStacked, scrolledPastHero, scrollY,
    saved, setSaved, saving, handleShare, handleWhatsAppShare, handleAddToCalendar, handleToggleSave,
    // Derived
    hostProfileKey, dateLabel, timeLabel, hostLabel, eventKey, priceLabel,
    ticketOptions, coverChargeCategory, lowestPriceCategory, isPaidEvent, startingFromPriceLabel,
    moodTag, hasTicketTiers, hasActiveSelected, canContinueCheckout,
    isOnlineEvent, isRestricted, eventStillRestricted, sortedPerformers, mediaItems, heroImages,
    stages, hasMultiStage, isFreeEvent, showPaymentMethods,
    hostStats, ratingLabel, tags, ruleItems,
    mapCoordinates, mapQuery, mapUrl, mapImageUrl,
    capacityTotal, spotsLeft, isPromoted, urgencyTier,
    capacityLeftLabel, capacityTotalLabel, percentSold,
    isApprovalEvent, needsRequest, isEnded, isCancelled, canRsvp,
    primaryCtaLabel, invitesLeft: invitesLeft, canInvite, isModalOpen,
  };
}

/* ------------------------------------------------------------------ */
/*  Utility functions                                                   */
/* ------------------------------------------------------------------ */

function formatDate(value?: string | null) {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Asia/Kolkata" });
}

function formatTime(value?: string | null) {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" }) + " IST";
}
